import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http } from "@/lib/api";
import { ArrowLeft, ArrowRight, Check, Play } from "lucide-react";

export default function CaseStudy() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    http.get(`/case-studies/${slug}`).then(({ data }) => setC(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="p-24 text-center text-gray-500">Case study not found.</div>;
  if (!c) return <div className="p-24 text-center text-gray-500">Loading…</div>;

  return (
    <article data-testid={`case-study-${slug}`}>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="aspect-[21/10] w-full overflow-hidden">
          <img src={c.hero_image} alt={c.client} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
        </div>
        <div className="absolute inset-x-0 bottom-0">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-10 lg:pb-14">
            <Link to="/studio#work" className="inline-flex items-center gap-2 text-xs text-gray-300 hover:text-white tracking-[0.28em] uppercase mb-6">
              <ArrowLeft size={12} /> Back to work
            </Link>
            <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-gray-300 mb-3">
              <span className="text-[#A855F7]">{c.tag}</span>
              <span>·</span>
              <span>{c.year}</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight font-display-media italic leading-[0.95]">
              {c.client}
            </h1>
            <div className="text-lg text-gray-300 mt-3">{c.project}</div>
          </div>
        </div>
      </section>

      {/* VIDEO / BRIEF */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            {c.video_url ? (
              <div className="glass rounded-2xl overflow-hidden aspect-video">
                <iframe src={c.video_url} title={c.project} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen className="w-full h-full" />
              </div>
            ) : (
              <div className="glass rounded-2xl overflow-hidden aspect-video grid place-items-center bg-black" data-testid="case-video-placeholder">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border hairline mb-3" style={{ borderColor: "#A855F7" }}>
                    <Play size={20} className="text-[#A855F7] ml-0.5" />
                  </div>
                  <div className="text-sm text-gray-400">Commercial reel · coming online</div>
                </div>
              </div>
            )}
          </div>
          <div className="lg:col-span-5">
            <div className="label-eyebrow mb-3">The brief</div>
            <p className="text-base text-gray-200 leading-relaxed">{c.brief}</p>

            {c.deliverables?.length > 0 && (
              <div className="mt-8">
                <div className="label-eyebrow mb-3">Deliverables</div>
                <ul className="space-y-2">
                  {c.deliverables.map((d) => (
                    <li key={d} className="flex items-start gap-2 text-sm text-gray-300">
                      <Check size={14} className="text-[#A855F7] mt-1 flex-shrink-0" /> {d}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {c.outcome && (
              <div className="mt-8 border-t hairline pt-6">
                <div className="label-eyebrow mb-3">Outcome</div>
                <p className="text-sm text-gray-300 leading-relaxed">{c.outcome}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CREATIVE BREAKDOWN / GALLERY */}
      {c.gallery?.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20">
          <div className="label-eyebrow mb-6">Creative breakdown</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {c.gallery.map((img, i) => (
              <div key={i} className="rounded-2xl overflow-hidden border hairline aspect-[4/3]">
                <img src={img} alt={`${c.client} — frame ${i + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 text-center">
        <h2 className="text-4xl sm:text-6xl font-black tracking-tight font-display-media italic">
          Let's build <span className="text-[#A855F7]">something similar.</span>
        </h2>
        <p className="text-gray-400 mt-5 max-w-2xl mx-auto leading-relaxed">
          Every engagement starts with a complimentary strategy call.
        </p>
        <Link to="/studio#book" data-testid="case-cta-book" className="mt-8 btn-purple inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm">
          Book Strategy Call <ArrowRight size={16} />
        </Link>
      </section>
    </article>
  );
}
